import React from 'react';

/**
 * StickerText — renders text with a bold black outline and a soft
 * transparent-white outer outline, giving it a "sticker" look.
 *
 * Usage as a component:
 *   <StickerText className="text-lg font-black" whiteOpacity={0.35}>
 *     Hello world
 *   </StickerText>
 *
 * Usage as a className helper:
 *   import { stickerTextStyle } from './StickerText';
 *   <span style={stickerTextStyle(0.35)}>Hello world</span>
 */

export interface StickerTextProps extends React.HTMLAttributes<HTMLSpanElement> {
    /** Opacity of the white outer outline. Defaults to 0.35 (uses CSS var if not set). */
    whiteOpacity?: number;
    /** Tag to render as. Defaults to "span". */
    as?: React.ElementType;
    children: React.ReactNode;
}

/** Returns an inline style object that gives text the sticker outline effect.
 *  - Black outline: tight 4-direction offset shadows (sharp edge)
 *  - White glow: centered Gaussian blur — opacity-sensitive "backlit" halo
 */
export const stickerTextStyle = (whiteOpacity: number = 0.35): React.CSSProperties => {
    const bk = '0.8px'; // black outline offset

    // Sharp black outline in 4 diagonal directions
    const blackOutline = [
        `-${bk} -${bk} 0 #000`,
        ` ${bk} -${bk} 0 #000`,
        `-${bk}  ${bk} 0 #000`,
        ` ${bk}  ${bk} 0 #000`,
    ].join(', ');

    // Centered glow — blur radius controls spread, opacity controls intensity
    // This is what actually responds to whiteOpacity changes
    const glowRadius = '3px';
    const whiteGlow = `0 0 ${glowRadius} rgba(255,255,255,${whiteOpacity})`;

    // Black renders on top (listed first), white glow sits behind it
    return {
        textShadow: [blackOutline, whiteGlow].join(', '),
    };
};

const StickerText: React.FC<StickerTextProps> = ({
    whiteOpacity,
    as: Tag = 'span',
    style,
    children,
    ...rest
}) => {
    const opacity = whiteOpacity ?? 0.35;
    return (
        <Tag
            {...rest}
            style={{ ...stickerTextStyle(opacity), ...style }}
        >
            {children}
        </Tag>
    );
};

export default StickerText;
